import React, { useState, useMemo } from 'react';
import {
  Box,
  IconButton,
  Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, TableSortLabel,
  TextField,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { StringType, Stringing, Material } from '../types';
import EditStringTypeForm from './EditStringTypeForm';

interface Props {
  stringTypes: StringType[];
  stringings: Stringing[];
  onAddNewString: () => void;
  onUpdated: () => void;
}

function sentenceCase(value: string): string {
  if (!value) return '';
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

// Column definitions: id = data key, label = header text, get = accessor, type for sorting
const COLUMNS = [
  { id: 'brand',         label: 'Brand',           get: (s: any) => s.brand ?? '',           type: 'string' as const },
  { id: 'name',          label: 'Name',            get: (s: any) => s.name,                  type: 'string' as const },
  { id: 'gauge',         label: 'Gauge',           get: (s: any) => s.gauge ?? '',           type: 'string' as const },
  { id: 'material',      label: 'Material',        get: (s: any) => s.material,              type: 'string' as const },
  { id: 'spinPotential', label: 'Spin Potential',  get: (s: any) => s.spinPotential ?? null, type: 'number' as const },
  { id: 'stiffness',     label: 'Stiffness',       get: (s: any) => s.stiffness ?? null,     type: 'number' as const },
  { id: 'color',         label: 'Color',           get: (s: any) => s.color ?? '',           type: 'string' as const },
  { id: 'usage',         label: 'Usage',           get: (s: any) => s.usage ?? 0,            type: 'number' as const },
];

type SortOrder = 'asc' | 'desc';

export default function StringsTable({ stringTypes, stringings, onAddNewString, onUpdated }: Props) {
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<string>('brand');  // must match a COLUMNS id
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [page, setPage] = useState(0);
  const rowsPerPage = 100;

  const [editingString, setEditingString] = useState<StringType | null>(null);

  // Enhance rows with "usage"
  const withUsage = useMemo(() => {
    const counts = new Map<string, number>();
    for (const str of stringings) {
      if (str.mainsId) counts.set(str.mainsId, (counts.get(str.mainsId) ?? 0) + 1);
      if (str.crossesId) counts.set(str.crossesId, (counts.get(str.crossesId) ?? 0) + 1);
    }
    return stringTypes.map(s => ({ ...s, usage: counts.get(s.id) ?? 0 }));
  }, [stringTypes, stringings]);

  // Filter by brand/name
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return withUsage;
    return withUsage.filter(s =>
      (s.brand ?? '').toLowerCase().includes(q) ||
      s.name.toLowerCase().includes(q)
    );
  }, [withUsage, search]);

  // Sort with correct column + type, nulls last, numeric aware
  const sorted = useMemo(() => {
    const col = COLUMNS.find(c => c.id === sortBy) ?? COLUMNS[0];

    const cmp = (a: any, b: any) => {
      const av = col.get(a);
      const bv = col.get(b);

      const aNull = av === null || av === undefined || av === '';
      const bNull = bv === null || bv === undefined || bv === '';
      if (aNull && bNull) return 0;
      if (aNull) return 1;         // nulls/empties last
      if (bNull) return -1;

      if (col.type === 'number') {
        const diff = (av as number) - (bv as number);
        return diff === 0 ? 0 : diff < 0 ? -1 : 1;
      }
      // string / enum
      return String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
    };

    const result = [...filtered].sort(cmp);
    if (sortOrder === 'desc') result.reverse();
    return result;
  }, [filtered, sortBy, sortOrder]);

  const paginatedRows = useMemo(
    () => sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sorted, page]
  );

  const handleRowClick = (s: StringType) => setEditingString(s);

  const handleSort = (colId: string) => {
    setPage(0);
    if (sortBy === colId) {
      setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(colId);
      setSortOrder('asc');
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" my={2}>
        <TextField
          label="Search strings"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          size="small"
          sx={{ flex: 1, mr: 2 }}
        />

        <IconButton
          onClick={onAddNewString}
          sx={{
            borderRadius: '50%',
            backgroundColor: '#282c34',
            width: 40, height: 40,
            '&:hover': {
              backgroundColor: '#405a52',
              '& svg': { transform: 'scale(1.2)' },
            },
            '& svg': { transition: 'transform 0.2s ease-in-out' },
          }}
        >
          <AddIcon sx={{ color: '#fff' }} />
        </IconButton>
      </Box>

      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {COLUMNS.map(col => (
                <TableCell key={col.id}>
                  <TableSortLabel
                    active={sortBy === col.id}
                    direction={sortBy === col.id ? sortOrder : 'asc'}
                    onClick={() => handleSort(col.id)}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell />
            </TableRow>
          </TableHead>

          <TableBody>
            {paginatedRows.map((s) => (
              <TableRow
                key={s.id}
                hover
                onClick={() => handleRowClick(s)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>{s.brand ?? ''}</TableCell>
                <TableCell>{s.name}</TableCell>
                <TableCell>{s.gauge ?? ''}</TableCell>
                <TableCell>{sentenceCase(String(s.material))}</TableCell>
                <TableCell>{s.spinPotential ?? ''}</TableCell>
                <TableCell>{s.stiffness ?? ''}</TableCell>
                <TableCell>{s.color ?? ''}</TableCell>
                <TableCell>{(s as any).usage}</TableCell>

                <TableCell align="right">
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); setEditingString(s); }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={sorted.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[rowsPerPage]}
        />
      </TableContainer>

      <EditStringTypeForm
        open={!!editingString}
        onClose={() => setEditingString(null)}
        existing={editingString}
        onUpdated={onUpdated}
      />
    </Box>
  );
}
